import { Component } from '@theme/component'
import { MediaStartedPlayingEvent, ThemeEvents } from '@theme/events'
import { mediaQueryMobile } from '@theme/use-device-detection'
import { useIntersection } from '@theme/use-intersection'

const root = globalThis
const theme = root.theme || {}
root.theme = theme
theme.config = theme.config || {}
theme.utils = theme.utils || {}

theme.config.isTouch ??= 'ontouchstart' in root || navigator.maxTouchPoints > 0
theme.config.motionReduced ??=
  root.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false
theme.config.mqlSmall ??=
  root.matchMedia?.('(max-width: 749px)')?.matches ?? false

theme.InitWhenVisible ??= class {
  constructor(callback) {
    if (typeof callback === 'function') {
      callback()
    }
  }
}

theme.utils.imageLoaded ??= (images) => {
  if (!images) {
    return Promise.resolve()
  }
  const elements = images instanceof Element ? [images] : Array.from(images)
  return Promise.all(
    elements.map(
      (image) =>
        new Promise((resolve) => {
          if (
            (image.tagName === 'IMG' && image.complete) ||
            image.tagName === 'VIDEO' ||
            image.tagName === 'IFRAME'
          ) {
            resolve()
            return
          }
          image.addEventListener('load', () => resolve(), {
            once: true
          })
          image.addEventListener('error', () => resolve(), {
            once: true
          })
        })
    )
  )
}

const MediaConfig = {
  markLoadedIdleTimeoutMs: 150,
  maxContentWaitAttempts: 20
}

const requestIdle =
  root.requestIdleCallback ||
  function fallback(callback) {
    return setTimeout(callback, 1)
  }

export class UiLazyImage extends HTMLImageElement {
  constructor() {
    super()
    this.handleLazy = this.handleLazy.bind(this)
    this.onLoad = this.onLoad.bind(this)
    this.onError = this.onError.bind(this)
  }

  connectedCallback() {
    if (!this.media) {
      return
    }

    this.mutationObserver = new MutationObserver(() => {
      this.classList.remove('loaded')
      this.handleLazy()
    })

    this.mutationObserver.observe(this, {
      attributeFilter: ['src', 'srcset'],
      attributes: true
    })
    this.handleLazy()
  }

  disconnectedCallback() {
    this.mutationObserver?.disconnect()
  }

  get media() {
    this._media = this._media || this.closest('.media')
    return this._media
  }

  handleLazy() {
    if (this.classList.contains('loaded')) {
      return
    }
    this.media?.classList.add('loading')

    if (this.complete) {
      this.onLoad()
      return
    }

    this.addEventListener('load', this.onLoad, { once: true })
    this.addEventListener('error', this.onError, { once: true })
  }

  onLoad() {
    const markLoaded = () => {
      this.classList.add('loaded')
      this.media?.classList.remove('loading')
    }
    requestIdle(markLoaded, { timeout: MediaConfig.markLoadedIdleTimeoutMs })
  }

  onError() {
    this.media?.classList.remove('loading')
  }
}

export class UiMedia extends Component {
  connectedCallback() {
    super.connectedCallback()
  }

  get media() {
    return Array.from(
      this.querySelectorAll(
        'picture>img, video, iframe, svg, ui-video, ui-video-media, ui-deferred-media, g-map'
      )
    )
  }
}

export class DeferredMedia extends Component {
  #mediaAbortController = null
  #visibilityObserver = null
  #intersectionCleanup = null
  #pausedByViewport = false

  isPlaying = false

  static get observedAttributes() {
    return ['playing']
  }

  get posterElement() {
    return this.querySelector('[id^="DeferredPoster-"]')
  }

  get controlledElement() {
    return this.hasAttribute('aria-controls')
      ? document.getElementById(this.getAttribute('aria-controls'))
      : null
  }

  get autoplay() {
    return this.hasAttribute('autoplay')
  }

  get playing() {
    return this.hasAttribute('playing')
  }

  get loaded() {
    return this.hasAttribute('loaded') || this.hasAttribute('data-media-loaded')
  }

  get player() {
    if (this.playerProxy) {
      return this.playerProxy
    }
    this.playerProxy = new Proxy(this.playerTarget(), {
      get: (target, prop) => {
        return async () => {
          const resolvedTarget = await target
          this.playerHandler(resolvedTarget, prop)
        }
      }
    })
    return this.playerProxy
  }

  onPosterClick = (event) => {
    event.preventDefault()
    event.stopPropagation()
    this.toggleMedia()
  }

  showDeferredMedia(event) {
    event?.preventDefault()
    event?.stopPropagation()
    this.playMedia()
  }

  loadContent(focus = true) {
    const player = this.playerTarget()
    this.markMediaLoaded()

    if (focus && player instanceof HTMLElement) {
      player.focus()
    }

    return player
  }

  toggleMedia(event) {
    event?.preventDefault()
    event?.stopPropagation()

    if (this.playing) {
      this.pauseMedia()
      return
    }

    this.playMedia()
  }

  playMedia() {
    this.paused = false
    this.play()
  }

  pauseMedia() {
    this.paused = true
    this.pause()
    this.isPlaying = false
    this.updatePlayPauseHint(false)
  }

  updatePlayPauseHint(isPlaying) {
    const toggleMediaButton = this.refs?.toggleMediaButton
    if (toggleMediaButton instanceof HTMLElement) {
      toggleMediaButton.classList.remove('hidden')
      toggleMediaButton
        .querySelector('.icon-play')
        ?.classList.toggle('hidden', isPlaying)
      toggleMediaButton
        .querySelector('.icon-pause')
        ?.classList.toggle('hidden', !isPlaying)
    }
  }

  markMediaLoaded() {
    this.setAttribute('loaded', '')
    this.setAttribute('data-media-loaded', 'true')
    this.refs?.deferredMediaPlayButton?.classList.add('deferred-media__playing')
    this.closest('.media')?.classList.remove('loading')
  }

  handleMediaStartedPlaying = (event) => {
    if (event.detail?.resource === this) {
      return
    }
    this.pauseMedia()
  }

  connectedCallback() {
    super.connectedCallback()
    this.#mediaAbortController = new AbortController()
    const { signal } = this.#mediaAbortController

    this.posterElement?.addEventListener('click', this.onPosterClick, {
      signal
    })

    if (!this.autoplay) {
      document.addEventListener(
        ThemeEvents.mediaStartedPlaying,
        this.handleMediaStartedPlaying,
        { signal }
      )
      document.addEventListener('dialog:close', this.pauseMedia.bind(this), {
        signal
      })
    }

    if (this.playing) {
      return
    }

    if (this.getAttribute('loading') === 'eager') {
      this._waitForContentAndInit()
    } else if (this.autoplay) {
      this._setupAutoplayVisibility()
    }
  }

  disconnectedCallback() {
    this.#mediaAbortController?.abort()
    this.#visibilityObserver?.disconnect()
    this.#intersectionCleanup?.()
    super.disconnectedCallback()
  }

  get _autoplaySuppressed() {
    return mediaQueryMobile.matches || globalThis.theme?.config?.motionReduced
  }

  _setupAutoplayVisibility() {
    if (this._autoplaySuppressed) {
      this._enableTapToPlay()
      return
    }

    const observer = useIntersection({ rootMargin: '200px 0px 200px 0px' })
    this.#intersectionCleanup = observer.observe(this, (entry) => {
      this._onViewportChange(entry.isIntersecting)
    })
  }

  _onViewportChange(isIntersecting) {
    if (isIntersecting) {
      if (this.paused) {
        return
      }
      if (!this.isInitialized) {
        this._waitForContentAndInit()
      } else if (this.#pausedByViewport) {
        this.#pausedByViewport = false
        this.play()
      }
      return
    }

    if (this.playing) {
      this.#pausedByViewport = true
      this.pause()
    }
  }

  _enableTapToPlay() {
    this.addEventListener(
      'click',
      (event) => {
        if (this.isInitialized || event.target.closest('a')) {
          return
        }
        this.paused = false
        this._waitForContentAndInit()
      },
      { signal: this.#mediaAbortController?.signal }
    )
  }

  init() {
    if (this.isInitialized) {
      return
    }
    this.isInitialized = true

    if (!this.paused) {
      this.play()
    }
    return () => {
      this.pause()
    }
  }

  play() {
    if (!this.playing) {
      this.player.play()
    }
  }

  pause() {
    if (this.playing) {
      this.player.pause()
    }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'playing') {
      if (oldValue === null && newValue === '') {
        this.isPlaying = true
        this.dispatchEvent(new CustomEvent('video:play', { bubbles: true }))
      } else if (newValue === null) {
        this.isPlaying = false
        this.dispatchEvent(new CustomEvent('video:pause', { bubbles: true }))
      }
    }
  }

  _waitForContentAndInit(attempts = 0) {
    if (this.querySelector('template') || this.querySelector('video')) {
      this.init()
      return
    }

    if (attempts > MediaConfig.maxContentWaitAttempts) {
      return
    }

    requestIdle(() => this._waitForContentAndInit(attempts + 1))
  }
}

export class UiVideo extends DeferredMedia {
  #attachedVideoListeners = new WeakSet()

  constructor() {
    super()
    this._manualControl = false
    this._setupCustomControls()
    this._setupHoverPlay()
  }

  get hoverPlay() {
    return this.hasAttribute('hover-play')
  }

  connectedCallback() {
    if (this.hoverPlay && !this.autoplay) {
      this.paused = true
    }

    super.connectedCallback()

    if (this.hoverPlay && !this.autoplay) {
      this._waitForContentAndInit()
    }

    this._updatePlayPauseIcons()
    this._updateMuteIcons(this.querySelector('video')?.muted ?? false)
  }

  get isLocalDevHost() {
    const hostname = globalThis.location?.hostname
    return (
      hostname === 'localhost' ||
      hostname === '127.0.0.1' ||
      hostname === '0.0.0.0'
    )
  }

  get localStoreOrigin() {
    if (!this.isLocalDevHost) {
      return null
    }

    if (globalThis.Shopify?.shop) {
      return `https://${globalThis.Shopify.shop}`
    }

    const featuresElement = document.getElementById('shopify-features')
    if (!featuresElement?.textContent) {
      return null
    }

    try {
      const features = JSON.parse(featuresElement.textContent)
      if (!features?.domain) {
        return null
      }
      return `https://${features.domain}`
    } catch {
      return null
    }
  }

  normalizeLocalVideoUrl(url) {
    if (!url) {
      return url
    }

    const storeOrigin = this.localStoreOrigin
    if (!storeOrigin) {
      return url
    }

    if (url.startsWith('/cdn/')) {
      return `${storeOrigin}${url}`
    }

    if (url.startsWith('//cdn/')) {
      return `${storeOrigin}/cdn/${url.slice(6)}`
    }

    return url
  }

  normalizeLocalVideoNode(videoNode) {
    if (!(videoNode instanceof HTMLElement)) {
      return
    }

    if (videoNode.tagName !== 'VIDEO') {
      return
    }

    const sourceUrl = videoNode.getAttribute('src')
    if (sourceUrl) {
      videoNode.setAttribute('src', this.normalizeLocalVideoUrl(sourceUrl))
    }

    const posterUrl = videoNode.getAttribute('poster')
    if (posterUrl) {
      videoNode.setAttribute('poster', this.normalizeLocalVideoUrl(posterUrl))
    }

    for (const source of videoNode.querySelectorAll('source')) {
      const sourceSrc = source.getAttribute('src')
      if (sourceSrc) {
        source.setAttribute('src', this.normalizeLocalVideoUrl(sourceSrc))
      }
    }
  }

  _setupHoverPlay() {
    if (!this.hoverPlay) return

    this.addEventListener('mouseenter', () => {
      if (!this._manualControl) {
        this.paused = false
        this.play()
      }
    })

    this.addEventListener('mouseleave', () => {
      if (!this._manualControl) {
        this.paused = true
        this.pause()
      }
    })
  }

  _setupCustomControls() {
    const playPauseBtn = this.querySelector('[data-action="play-pause"]')
    const muteBtn = this.querySelector('[data-action="toggle-mute"]')

    if (playPauseBtn) {
      playPauseBtn.addEventListener('click', (e) => {
        e.preventDefault()
        e.stopPropagation()
        this._togglePlayPause()
      })
    }

    if (muteBtn) {
      muteBtn.addEventListener('click', (e) => {
        e.preventDefault()
        e.stopPropagation()
        this._toggleMute()
      })
    }
  }

  _togglePlayPause() {
    this._manualControl = true

    if (this.playing) {
      this.paused = true
      this.pause()
      this._manualControl = false
    } else {
      this.paused = false
      this.play()
    }
    this._updatePlayPauseIcons()
  }

  _toggleMute() {
    const video = this.querySelector('video')
    if (video) {
      video.muted = !video.muted
      this._updateMuteIcons(video.muted)
    }
  }

  _updatePlayPauseIcons() {
    this.querySelector('[data-action="play-pause"]')?.classList.toggle(
      'is-secondary-icon',
      this.playing
    )
  }

  _updateMuteIcons(isMuted) {
    this.querySelector('[data-action="toggle-mute"]')?.classList.toggle(
      'is-secondary-icon',
      isMuted
    )
  }

  playerTarget() {
    const existingPlayer = this.querySelector('video, iframe[data-video-type]')
    if (existingPlayer) {
      this._attachVideoListeners(existingPlayer)
      this.markMediaLoaded()
      return existingPlayer
    }

    const templateElement = this.querySelector('template')
    if (templateElement) {
      const playerNode =
        templateElement.content.firstElementChild?.cloneNode(true)
      if (playerNode) {
        this.normalizeLocalVideoNode(playerNode)
        this.appendChild(playerNode)
      }
    } else {
      const player = this.querySelector('video')
      if (!player) {
        return {
          mute: () => undefined,
          pause: () => undefined,
          play: () => undefined
        }
      }
    }

    this.markMediaLoaded()

    const player = this.querySelector('video, iframe[data-video-type]')
    if (player) {
      this._attachVideoListeners(player)
    }
    return player
  }

  playerHandler(target, prop) {
    if (target instanceof HTMLIFrameElement) {
      this.postIframeMediaCommand(target, prop)
      return
    }

    if (prop === 'play') {
      const playPromise = target.play()
      if (playPromise instanceof Promise) {
        playPromise.catch((error) => {
          if (error.name === 'NotAllowedError') {
            this.setAttribute('suspended', '')
            target.controls = true
            const replacementImageSrc =
              target.previousElementSibling?.currentSrc
            if (replacementImageSrc) {
              target.poster = replacementImageSrc
            }
          }
        })
      }
    } else if (typeof target[prop] === 'function') {
      target[prop]()
    }
  }

  postIframeMediaCommand(target, prop) {
    if (prop === 'play') {
      this.dispatchEvent(new MediaStartedPlayingEvent(this))
      this.setAttribute('playing', '')
      this.updatePlayPauseHint(true)
      target.contentWindow?.postMessage(
        target.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"playVideo","args":""}'
          : '{"method":"play"}',
        '*'
      )
      return
    }

    if (prop === 'pause') {
      this.removeAttribute('playing')
      this.updatePlayPauseHint(false)
      target.contentWindow?.postMessage(
        target.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"pauseVideo","args":""}'
          : '{"method":"pause"}',
        '*'
      )
    }
  }

  _attachVideoListeners(player) {
    if (!(player instanceof HTMLVideoElement)) {
      return player
    }

    if (this.#attachedVideoListeners.has(player)) {
      return player
    }
    this.#attachedVideoListeners.add(player)

    if (this.autoplay && player.loop) {
      player.muted = true
    }

    player.addEventListener('play', () => {
      const suppressUnexpectedAutoplay =
        this.hoverPlay && !this._videoStarted && this.paused

      if (suppressUnexpectedAutoplay) {
        this._videoStarted = true
        player.pause()
        return
      }

      this._videoStarted = true
      this.dispatchEvent(new MediaStartedPlayingEvent(this))
      this.setAttribute('playing', '')
      this.removeAttribute('suspended')
      this._updatePlayPauseIcons()
      this.updatePlayPauseHint(true)
    })
    player.addEventListener('pause', () => {
      if (!player.seeking && player.paused) {
        this.removeAttribute('playing')
        this._updatePlayPauseIcons()
        this.updatePlayPauseHint(false)
      }
    })
    player.addEventListener('volumechange', () => {
      this._updateMuteIcons(player.muted)
    })
    return player
  }
}

export function playCardMedia(media) {
  if (!(media instanceof HTMLElement)) return
  if (typeof media.loadContent === 'function') media.loadContent(false)
  requestAnimationFrame(() => {
    const video = media.querySelector('video')
    if (video instanceof HTMLVideoElement) {
      video.pause()
      try {
        video.currentTime = 0
        // biome-ignore lint/suspicious/noEmptyBlockStatements: intentional no-op
      } catch {}
      video.play().catch(() => undefined)
      if (typeof media.updatePlayPauseHint === 'function') {
        media.updatePlayPauseHint(true)
      }
      media.isPlaying = true
    }
  })
}

export function pauseCardMedia(media) {
  if (!(media instanceof HTMLElement)) return
  if (typeof media.pauseMedia === 'function') media.pauseMedia()
  requestAnimationFrame(() => {
    const video = media.querySelector('video')
    if (video instanceof HTMLVideoElement) {
      try {
        video.currentTime = 0
        // biome-ignore lint/suspicious/noEmptyBlockStatements: intentional no-op
      } catch {}
    }
  })
}

export class ProductCardMediaHover {
  #host
  #abortController = null

  constructor(host) {
    this.#host = host
  }

  setup(isInitial = false) {
    this.teardown()
    const gallery = this.#host.querySelector('.product-card-gallery')
    if (!gallery) return

    if (document.body.classList.contains('no-card-media-hover')) {
      if (!isInitial) {
        requestAnimationFrame(() => {
          for (const media of this.#getHoverableMedia()) {
            pauseCardMedia(media)
          }
        })
      }
      return
    }

    this.#abortController = new AbortController()
    const { signal } = this.#abortController

    gallery.addEventListener('pointerenter', this.#handlePointerEnter, {
      signal
    })
    gallery.addEventListener('pointerleave', this.#handlePointerLeave, {
      signal
    })

    if (!isInitial) {
      requestAnimationFrame(() => {
        for (const media of this.#getHoverableMedia()) {
          pauseCardMedia(media)
        }
      })
    }
  }

  teardown() {
    this.#abortController?.abort()
    this.#abortController = null
  }

  preload() {
    for (const media of this.#getHoverableMedia()) {
      if (typeof media.loadContent === 'function') {
        media.loadContent(false)
      }
    }
  }

  #getHoverableMedia() {
    return this.#host.querySelectorAll(
      '.product-card-media__reveal ui-video, .product-card-media__reveal ui-video-media, .product-card-media__reveal ui-deferred-media'
    )
  }

  #handlePointerEnter = (event) => {
    if (!event.pointerType || event.pointerType === 'mouse') {
      for (const media of this.#getHoverableMedia()) {
        playCardMedia(media)
      }
    }
  }

  #handlePointerLeave = (event) => {
    if (!event.pointerType || event.pointerType === 'mouse') {
      for (const media of this.#getHoverableMedia()) {
        pauseCardMedia(media)
      }
    }
  }
}

class MediaElement extends UiMedia {}

class UiVideoMedia extends UiVideo {}

class UiDeferredMedia extends UiVideo {}

if (!customElements.get('ui-lazy-image')) {
  customElements.define('ui-lazy-image', UiLazyImage, { extends: 'img' })
}
if (!customElements.get('media-element')) {
  customElements.define('media-element', MediaElement)
}
if (!customElements.get('ui-media')) {
  customElements.define('ui-media', UiMedia)
}
if (!customElements.get('ui-video')) {
  customElements.define('ui-video', UiVideo)
}
if (!customElements.get('ui-video-media')) {
  customElements.define('ui-video-media', UiVideoMedia)
}
if (!customElements.get('ui-deferred-media')) {
  customElements.define('ui-deferred-media', UiDeferredMedia)
}
